import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Smartphone, Laptop, Tablet, Monitor, Speaker, Zap, Shield, Globe, CheckCircle2, AlertTriangle, Upload, X, Phone, Mail, FileText, Battery, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';

const ElectronicsBatteryShippingPage = () => {
  const { toast } = useToast();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // Smooth spring animations
  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const smoothProgress = useSpring(scrollYProgress, springConfig);

  // Device animations with different timings for varied effect
  const phoneRotate = useTransform(smoothProgress, [0, 0.4], [0, 360]);
  const phoneY = useTransform(smoothProgress, [0, 0.2, 0.4], [0, -50, -150]);
  const phoneScale = useTransform(smoothProgress, [0, 0.15, 0.3], [1, 1.15, 0.95]);

  const laptopRotate = useTransform(smoothProgress, [0.1, 0.5], [-10, 25]);
  const laptopY = useTransform(smoothProgress, [0.1, 0.3, 0.5], [0, -80, -200]);
  const laptopOpenAngle = useTransform(smoothProgress, [0.1, 0.4], [30, 120]);

  const tabletY = useTransform(smoothProgress, [0.15, 0.35, 0.55], [0, -60, -180]);
  const tabletRotate = useTransform(smoothProgress, [0.15, 0.55], [0, -15]);

  const speakerScale = useTransform(smoothProgress, [0.2, 0.4], [1, 1.2]);
  const speakerY = useTransform(smoothProgress, [0.2, 0.6], [0, -250]);

  // Product Eligibility Form State
  const [eligibilityForm, setEligibilityForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    originCity: '',
    originCountry: 'Canada',
    destinationCountry: '',
    productCategory: 'Smartphone',
    productBrand: '',
    productModel: '',
    quantity: '',
    approximateWeight: '',
    productCondition: 'New',
    hasBattery: 'Yes',
    batteryType: '',
    batteryCapacity: '',
    batteryPackaging: 'Installed in device',
    productDescription: '',
    additionalComments: ''
  });

  const [uploadedImages, setUploadedImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleEligibilityChange = (e) => {
    setEligibilityForm({
      ...eligibilityForm,
      [e.target.name]: e.target.value
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  const addFiles = (files) => {
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      const maxSize = 5 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        toast({
          title: "Invalid File Type",
          description: `${file.name} is not a valid image type. Please upload JPG, PNG or WEBP.`,
          variant: "destructive"
        });
        return false;
      }

      if (file.size > maxSize) {
        toast({
          title: "File Too Large",
          description: `${file.name} exceeds 5MB. Please compress and try again.`,
          variant: "destructive"
        });
        return false;
      }

      return true;
    });

    if (uploadedImages.length + validFiles.length > 5) {
      toast({
        title: "Maximum Files Exceeded",
        description: "You can upload a maximum of 5 product images.",
        variant: "destructive"
      });
      return;
    }

    const newImages = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setUploadedImages([...uploadedImages, ...newImages]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const removeImage = (index) => {
    const newImages = [...uploadedImages];
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setUploadedImages(newImages);
  };

  const handleEligibilitySubmit = async (e) => {
    e.preventDefault();
    
    if (uploadedImages.length === 0) {
      toast({
        title: "Product Photos Required",
        description: "Please upload at least one photo of the product and battery specifications (if applicable).",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Backend integration pending - showing notification
      toast({
        title: "⚠️ Backend Integration Pending",
        description: "Product eligibility form UI is complete. Secure cloud storage integration required before enabling live submissions.",
        variant: "destructive"
      });

      setTimeout(() => {
        toast({
          title: "Submission Successful (Demo)",
          description: "Your electronics product details would be sent to Shippzy for review once backend storage is configured.",
        });
      }, 2000);

    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error.message || "An error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: Globe,
      title: "International Electronics Shipping",
      description: "Ship smartphones, laptops, tablets, computers, speakers, and gaming devices from Canada to India and worldwide."
    },
    {
      icon: Battery,
      title: "Lithium Battery Compliance",
      description: "Specialized handling for electronics with lithium-ion batteries. We navigate carrier restrictions and dangerous goods regulations."
    },
    {
      icon: Shield,
      title: "Secure Packaging & Insurance",
      description: "Professional packaging guidance for fragile electronics. Optional insurance coverage for high-value devices."
    },
    {
      icon: Zap,
      title: "Fast Carrier Networks",
      description: "Partner with DHL, FedEx, and UPS for reliable electronics shipping with real-time tracking and expedited services."
    }
  ];

  const acceptedElectronics = [
    "Smartphones and mobile phones (with installed batteries)",
    "Laptops and notebooks (lithium battery inside)",
    "Tablets and e-readers",
    "Desktop computers and components",
    "Bluetooth speakers and audio devices",
    "Gaming consoles (PlayStation, Xbox, Nintendo)",
    "Smartwatches and fitness trackers",
    "Cameras and photography equipment",
    "Headphones and earbuds",
    "Power banks (subject to capacity limits)"
  ];

  const batteryRestrictions = [
    "Lithium-ion batteries must be installed in devices or securely packaged",
    "Loose or spare lithium batteries require special approval",
    "Battery capacity limits vary by carrier and route",
    "Power banks typically limited to 100Wh (27,000mAh at 3.7V)",
    "Damaged, recalled, or swollen batteries are prohibited",
    "Some airlines restrict lithium metal batteries",
    "Devices must be powered off during transit",
    "Battery terminals must be protected from short-circuit"
  ];

  const faqs = [
    {
      question: "Can I ship my smartphone from Canada to India?",
      answer: "Yes, smartphones can generally be shipped internationally if the lithium-ion battery is installed in the device and meets carrier capacity limits. However, carrier approval is required, and you must declare the device accurately. Submit your phone model and battery specifications through our eligibility check form."
    },
    {
      question: "Are laptops allowed in international shipping?",
      answer: "Laptops with built-in lithium batteries are typically accepted for international shipping via air cargo, but restrictions apply based on battery capacity (usually under 100Wh). The laptop must be powered off, and the battery must be securely installed. Carrier-specific rules may vary."
    },
    {
      question: "Can I ship loose lithium batteries or power banks?",
      answer: "Loose lithium batteries face strict regulations. Power banks under 100Wh (approximately 27,000mAh) may be accepted if packaged correctly and declared as dangerous goods. Batteries over this limit are often prohibited. Contact us for specific guidance based on your battery specifications."
    },
    {
      question: "What information do I need to provide about batteries?",
      answer: "You must provide battery type (lithium-ion, lithium-polymer, etc.), capacity in Wh or mAh, voltage, whether the battery is installed or loose, and product model number. Photos of the battery label or specifications are required for review."
    },
    {
      question: "Are used electronics accepted for shipping?",
      answer: "Yes, used electronics can be shipped internationally if they are functional and comply with customs regulations. Declare the accurate condition and value. Ensure devices are properly packaged to prevent damage during transit."
    },
    {
      question: "How long does the electronics eligibility review take?",
      answer: "Our team reviews electronics submissions within 1-3 business days. Complex cases involving high-capacity batteries or restricted devices may require additional carrier confirmation. You will receive email notification once the review is complete."
    }
  ];

  return (
    <>
      <Helmet>
        <title>International Electronics & Battery Shipping From Canada | Shippzy</title>
        <meta name="description" content="Ship smartphones, laptops, tablets, computers, and electronics with lithium batteries from Canada to India and worldwide. Expert handling, carrier compliance, customs support." />
        <link rel="canonical" href="https://www.shippzy.ca/electronics-battery-shipping" />
      </Helmet>

      <div ref={containerRef} className="min-h-screen bg-white overflow-hidden">
        
        {/* Hero Section with Floating Electronics */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950">
          {/* Animated grid background */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)',
              backgroundSize: '50px 50px'
            }}></div>
          </div>

          {/* Floating Smartphone */}
          <motion.div
            style={{
              rotate: phoneRotate,
              y: phoneY,
              scale: phoneScale
            }}
            className="absolute right-1/4 top-1/4 w-32 h-56 pointer-events-none hidden lg:block z-10"
          >
            <div className="relative w-full h-full">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl shadow-2xl border-4 border-gray-800">
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-16 h-4 bg-gray-900 rounded-full"></div>
                <div className="absolute inset-6 bg-gradient-to-br from-blue-400/30 to-transparent rounded-2xl"></div>
                <Smartphone className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 text-white/60" />
              </div>
              <div className="absolute -inset-6 bg-blue-500/30 blur-2xl rounded-full"></div>
            </div>
          </motion.div>

          {/* Floating Laptop */}
          <motion.div
            style={{
              rotate: laptopRotate,
              y: laptopY
            }}
            className="absolute left-20 top-1/3 w-64 h-40 pointer-events-none hidden lg:block z-20"
          >
            <motion.div 
              style={{ rotateX: laptopOpenAngle }}
              className="relative w-full h-full perspective-1000"
            >
              {/* Laptop Base */}
              <div className="absolute bottom-0 w-full h-2 bg-gradient-to-r from-gray-700 to-gray-800 rounded-lg shadow-xl"></div>
              {/* Laptop Screen */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-lg shadow-2xl border-4 border-gray-700" style={{ transformOrigin: 'bottom' }}>
                <div className="absolute inset-3 bg-gradient-to-br from-blue-600/40 to-indigo-600/40 rounded-lg">
                  <Laptop className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 text-white/50" />
                </div>
              </div>
              <div className="absolute -inset-10 bg-blue-500/20 blur-3xl rounded-full"></div>
            </motion.div>
          </motion.div>

          {/* Floating Tablet */}
          <motion.div
            style={{
              y: tabletY,
              rotate: tabletRotate
            }}
            className="absolute right-32 bottom-1/4 w-48 h-64 pointer-events-none hidden lg:block z-10"
          >
            <div className="relative w-full h-full">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl border-4 border-gray-700">
                <div className="absolute inset-4 bg-gradient-to-br from-indigo-600/30 to-purple-600/30 rounded-xl">
                  <Tablet className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 text-white/60" />
                </div>
              </div>
              <div className="absolute -inset-6 bg-indigo-500/30 blur-2xl rounded-full"></div>
            </div>
          </motion.div>

          {/* Floating Speaker with Pulse Effect */}
          <motion.div
            style={{
              scale: speakerScale,
              y: speakerY
            }}
            className="absolute left-1/4 bottom-1/3 w-32 h-40 pointer-events-none hidden lg:block z-10"
          >
            <motion.div
              animate={{
                scale: [1, 1.05, 1]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="relative w-full h-full"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black rounded-3xl shadow-2xl border-4 border-gray-700">
                <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full bg-gray-800 border-4 border-gray-700"></div>
                <div className="absolute bottom-1/4 left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full bg-gray-800 border-4 border-gray-700"></div>
                <Speaker className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 text-blue-500/70" />
              </div>
              <motion.div 
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.3, 0.1, 0.3]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute -inset-8 bg-blue-500/40 blur-2xl rounded-full"
              ></motion.div>
            </motion.div>
          </motion.div>

          {/* Main Content */}
          <div className="relative z-30 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-block mb-6 px-6 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full"
              >
                <span className="text-blue-400 font-semibold text-sm">⚡ Electronics & Battery Shipping</span>
              </motion.div>

              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                Ship Electronics
                <br />
                <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-500 bg-clip-text text-transparent">
                  & Batteries Worldwide
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
                International shipping for smartphones, laptops, tablets, computers, and devices with lithium batteries from Canada to India and beyond.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-6 text-lg shadow-xl"
                  onClick={() => document.getElementById('eligibility-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Check Electronics Eligibility
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                  onClick={() => document.getElementById('battery-info').scrollIntoView({ behavior: 'smooth' })}
                >
                  Battery Shipping Guide
                </Button>
              </div>

              <div className="mt-12 flex items-center justify-center gap-8 text-gray-400 text-sm">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-500" />
                  <span>+1 (437) 616-0758</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-500" />
                  <span>ship@shippzy.ca</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-30"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="text-white text-sm flex flex-col items-center gap-2"
            >
              <span className="opacity-70">Scroll to explore</span>
              <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="w-1.5 h-1.5 bg-white rounded-full"
                ></motion.div>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Why Ship Electronics with Shippzy?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Expert handling of electronics, lithium battery compliance, and carrier partnerships
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-t-4 border-blue-600">
                      <CardHeader>
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <CardTitle className="text-xl">{benefit.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-600">{benefit.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Accepted Electronics & Battery Restrictions */}
        <section id="battery-info" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Accepted Electronics */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card className="h-full shadow-lg">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                      <CardTitle className="text-2xl">Accepted Electronics</CardTitle>
                    </div>
                    <p className="text-gray-600">Devices typically eligible for international shipping (subject to carrier approval)</p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {acceptedElectronics.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Battery Restrictions */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card className="h-full shadow-lg border-l-4 border-orange-500">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <AlertTriangle className="w-8 h-8 text-orange-600" />
                      <CardTitle className="text-2xl">Lithium Battery Restrictions</CardTitle>
                    </div>
                    <p className="text-gray-600">Important requirements for devices with batteries</p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {batteryRestrictions.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <Battery className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Disclaimer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-12 bg-orange-50 border-l-4 border-orange-500 p-6 rounded-lg"
            >
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2">Dangerous Goods Regulations Apply</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Lithium batteries are classified as dangerous goods by IATA (International Air Transport Association). Shipping electronics with lithium-ion or lithium-polymer batteries requires carrier approval, proper packaging, accurate documentation, and compliance with capacity restrictions. Damaged, defective, or recalled batteries are strictly prohibited. Battery specifications must be reviewed by Shippzy before confirming shipment eligibility.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Product Eligibility Check Form */}
        <section id="eligibility-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Check Electronics Eligibility
              </h2>
              <p className="text-xl text-gray-600">
                Submit product details and battery specifications for manual review
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="shadow-2xl border-t-4 border-blue-600">
                <CardContent className="p-8">
                  <form onSubmit={handleEligibilitySubmit} className="space-y-6">
                    
                    {/* Customer Information */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                        <Input
                          name="fullName"
                          value={eligibilityForm.fullName}
                          onChange={handleEligibilityChange}
                          required
                          placeholder="John Doe"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                        <Input
                          type="email"
                          name="email"
                          value={eligibilityForm.email}
                          onChange={handleEligibilityChange}
                          required
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                        <Input
                          type="tel"
                          name="phone"
                          value={eligibilityForm.phone}
                          onChange={handleEligibilityChange}
                          required
                          placeholder="+1 (123) 456-7890"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Shipping From (City) *</label>
                        <Input
                          name="originCity"
                          value={eligibilityForm.originCity}
                          onChange={handleEligibilityChange}
                          required
                          placeholder="Toronto, Brampton, etc."
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Destination Country *</label>
                      <select
                        name="destinationCountry"
                        value={eligibilityForm.destinationCountry}
                        onChange={handleEligibilityChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      >
                        <option value="">Select destination country</option>
                        <option value="India">India</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="United Arab Emirates">United Arab Emirates</option>
                        <option value="United States">United States</option>
                        <option value="Australia">Australia</option>
                        <option value="Pakistan">Pakistan</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Product Information */}
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Product Information</h3>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Product Category *</label>
                          <select
                            name="productCategory"
                            value={eligibilityForm.productCategory}
                            onChange={handleEligibilityChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          >
                            <option value="Smartphone">Smartphone / Mobile Phone</option>
                            <option value="Laptop">Laptop / Notebook</option>
                            <option value="Tablet">Tablet / E-Reader</option>
                            <option value="Computer">Desktop Computer / Components</option>
                            <option value="Speaker">Bluetooth Speaker / Audio Device</option>
                            <option value="Gaming Console">Gaming Console</option>
                            <option value="Smartwatch">Smartwatch / Fitness Tracker</option>
                            <option value="Camera">Camera / Photography Equipment</option>
                            <option value="Headphones">Headphones / Earbuds</option>
                            <option value="Power Bank">Power Bank / Portable Charger</option>
                            <option value="Other">Other Electronics</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Product Condition *</label>
                          <select
                            name="productCondition"
                            value={eligibilityForm.productCondition}
                            onChange={handleEligibilityChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          >
                            <option value="New">New (sealed box)</option>
                            <option value="New - Open Box">New (open box)</option>
                            <option value="Used - Like New">Used (like new)</option>
                            <option value="Used - Good">Used (good condition)</option>
                            <option value="Refurbished">Refurbished</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6 mt-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Brand *</label>
                          <Input
                            name="productBrand"
                            value={eligibilityForm.productBrand}
                            onChange={handleEligibilityChange}
                            required
                            placeholder="e.g., Apple, Samsung, Dell"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Model Number *</label>
                          <Input
                            name="productModel"
                            value={eligibilityForm.productModel}
                            onChange={handleEligibilityChange}
                            required
                            placeholder="e.g., iPhone 14 Pro, MacBook Air M2"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6 mt-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity *</label>
                          <Input
                            type="number"
                            name="quantity"
                            value={eligibilityForm.quantity}
                            onChange={handleEligibilityChange}
                            required
                            min="1"
                            placeholder="1"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Approximate Weight *</label>
                          <Input
                            name="approximateWeight"
                            value={eligibilityForm.approximateWeight}
                            onChange={handleEligibilityChange}
                            required
                            placeholder="e.g., 1.5 kg"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Battery Information */}
                    <div className="border-t pt-6 mt-6 bg-orange-50 p-6 rounded-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Battery className="w-5 h-5 text-orange-600" />
                        Battery Information (Critical)
                      </h3>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Does this product contain a battery? *</label>
                          <select
                            name="hasBattery"
                            value={eligibilityForm.hasBattery}
                            onChange={handleEligibilityChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          >
                            <option value="Yes">Yes</option>
                            <option value="No">No battery</option>
                          </select>
                        </div>

                        {eligibilityForm.hasBattery === 'Yes' && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Battery Type *</label>
                            <select
                              name="batteryType"
                              value={eligibilityForm.batteryType}
                              onChange={handleEligibilityChange}
                              required
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            >
                              <option value="">Select battery type</option>
                              <option value="Lithium-ion">Lithium-ion (Li-ion)</option>
                              <option value="Lithium-polymer">Lithium-polymer (LiPo)</option>
                              <option value="Lithium-metal">Lithium-metal</option>
                              <option value="Unknown">Unknown / Not specified</option>
                            </select>
                          </div>
                        )}
                      </div>

                      {eligibilityForm.hasBattery === 'Yes' && (
                        <>
                          <div className="grid md:grid-cols-2 gap-6 mt-6">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Battery Capacity</label>
                              <Input
                                name="batteryCapacity"
                                value={eligibilityForm.batteryCapacity}
                                onChange={handleEligibilityChange}
                                placeholder="e.g., 5000mAh or 18.5Wh"
                              />
                              <p className="text-xs text-gray-500 mt-1">Provide capacity in Wh or mAh (found on battery label)</p>
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Battery Packaging *</label>
                              <select
                                name="batteryPackaging"
                                value={eligibilityForm.batteryPackaging}
                                onChange={handleEligibilityChange}
                                required
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                              >
                                <option value="Installed in device">Installed in device</option>
                                <option value="Packed with device">Packed separately with device</option>
                                <option value="Loose battery only">Loose battery only (requires special approval)</option>
                              </select>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Product Description */}
                    <div className="mt-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Product Description *</label>
                      <textarea
                        name="productDescription"
                        value={eligibilityForm.productDescription}
                        onChange={handleEligibilityChange}
                        required
                        rows={4}
                        placeholder="Describe the product, its specifications, and any relevant details for shipping..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      ></textarea>
                    </div>

                    {/* Image Upload */}
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Product Photos *</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Upload photos of: product, model number label, battery specifications label (max 5 images, 5MB each)
                      </p>

                      <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`
                          border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300
                          ${isDragging 
                            ? 'border-blue-600 bg-blue-50' 
                            : 'border-gray-300 hover:border-blue-600 hover:bg-gray-50'
                          }
                        `}
                      >
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-700 font-semibold mb-2">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-sm text-gray-500">
                          JPG, PNG or WEBP (max 5MB per file)
                        </p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </div>

                      {uploadedImages.length > 0 && (
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                          {uploadedImages.map((image, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={image.preview}
                                alt={`Product ${index + 1}`}
                                className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <p className="text-xs text-gray-600 mt-1 truncate">{image.name}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Additional Comments */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Comments (Optional)</label>
                      <textarea
                        name="additionalComments"
                        value={eligibilityForm.additionalComments}
                        onChange={handleEligibilityChange}
                        rows={3}
                        placeholder="Any special requirements, questions, or additional information..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-6">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-6 text-lg font-semibold shadow-xl"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Submitting...
                          </span>
                        ) : (
                          'Submit for Review'
                        )}
                      </Button>

                      <p className="text-sm text-gray-600 text-center mt-4">
                        Our team will review your electronics submission within 1-3 business days and contact you regarding shipping eligibility and carrier requirements.
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Common questions about shipping electronics and lithium batteries
              </p>
            </motion.div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="shadow-lg hover:shadow-xl transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg text-gray-900 flex items-start gap-3">
                        <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                        <span>{faq.question}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready to Ship Your Electronics?
              </h2>
              <p className="text-xl text-gray-300 mb-10">
                Submit your device for eligibility review or contact our team for guidance
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold shadow-xl"
                  onClick={() => document.getElementById('eligibility-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Check Eligibility Now
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                  onClick={() => window.location.href = '/contact'}
                >
                  Contact Shippzy
                </Button>
              </div>

              <div className="mt-12 flex flex-col md:flex-row items-center justify-center gap-8 text-gray-400">
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-blue-500" />
                  <span>+1 (437) 616-0758</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-500" />
                  <span>ship@shippzy.ca</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default ElectronicsBatteryShippingPage;
